/**
 * The ONE conversion from a `UserMessage` to an engine-neutral send payload.
 * Both engines' `paramsFromUserMessage` call `prepareSend` (the extension in
 * `message-app-context.ts`, the pod in `message-send.ts`) and then map only to
 * their own library call.
 *
 * Mentions: a real WhatsApp mention (highlight + ping) renders only where the
 * body carries an `@<number>` token matching an entry in the message's
 * `mentionedJid`. Inline `@[Name](jid)` markup is rewritten to `@<number>`, and
 * for every structured mention whose token is missing, the token is appended
 * (one decision for both engines). An engine that sends a person in another
 * form than the caller wrote (the pod: `<pn>@c.us` → `<lid>@lid`) passes the
 * forms it resolved ({@link PrepareSendOptions}); a body token of any form is
 * then rewritten to the token of the jid actually sent (#334).
 *
 * Pure and dependency-free (Node + browser).
 */

import { EngineToolError } from '../interfaces/whatsapp-tools-commands.interface';
import { callerTokenFits } from '../scheduling/markers';
import type { ButtonReply, LinkPreview, UserMessage } from '../schemas/bt/user-message';

export interface PreparedSend {
    /** The recipient: `contactList[0].id`. */
    chatId: string;
    /** Body (or media caption) with inline mentions rewritten and missing tokens appended. */
    text: string;
    /** Every mentioned jid (inline markup ∪ structured `mentions`), first-seen order. */
    mentions: string[];
    /** Only when `isPoll` is set; blank options dropped, sorted by `order`. */
    poll?: { question: string; options: Array<{ id: number; text: string }>; allowMultiple: boolean };
    /** The reply target's wire key (`quotedMessageId`). For a `buttonReply` it is the MENU being answered. */
    quotedKey?: string;
    /** Only for a reply to an interactive menu: the selected option (no body text; `quotedKey` is required). */
    buttonReply?: ButtonReply;
    /** Only when `sendLinkPreview` is set. */
    linkPreview?: LinkPreview;
    media?: { url: string; type?: string | null; name?: string | null };
    /** The wire messageSecret token (`resolveWireSecret`). */
    wireSecret: string;
}

/** Engine-resolved identity forms for the mention rule. Plain lookups (the
 *  engine resolves them before the call, so prepareSend stays pure and sync). */
export interface PrepareSendOptions {
    /** Inline `@[Name](jid)` markup jid → the jid the engine sends for it (its
     *  socket form). That jid goes into `mentions` and its token into the body.
     *  Absent → the jid as written. */
    inlineJidOf?: Readonly<Record<string, string>>;
    /** A mentioned jid → its other forms (lid ↔ phone). A body token of any
     *  form is rewritten to the mentioned jid's own token. */
    mentionAliases?: Readonly<Record<string, readonly string[]>>;
}

const INLINE_MENTION = /@\[(.*?)\]\((.*?)\)/g;

/** The jids of the inline `@[Name](jid)` markup in `text`, in order. */
export function inlineMentionJids(text: string | null | undefined): string[] {
    return Array.from((text ?? '').matchAll(INLINE_MENTION), (m) => m[2]);
}

/** Bare number (mention token payload) of a jid: `972…@c.us` → `972…`. */
function bareNumber(jid: string): string {
    return String(jid ?? '').split('@')[0].trim();
}

function escapeRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** True when `text` carries the `@<number>` token as a real mention token —
 *  bounded so `@123` is not seen inside `@1234` (nor in an email `x@123`). */
function hasMentionToken(text: string, number: string): boolean {
    return new RegExp(`(^|[^0-9@])@${escapeRegExp(number)}($|[^0-9])`).test(text);
}

/** Rewrite every bounded `@<alias>` token to the mentioned jid's own `@<number>`
 *  token, for each alias form the engine resolved (device suffix dropped). */
function rewriteAliasTokens(text: string, mentionedJids: string[], aliases: PrepareSendOptions['mentionAliases']): string {
    if (!aliases || !text) return text;
    let body = text;
    for (const jid of mentionedJids) {
        const number = bareNumber(jid);
        for (const alias of aliases[jid] ?? []) {
            const aliasNumber = bareNumber(alias).split(':')[0];
            if (!number || !aliasNumber || aliasNumber === number) continue;
            body = body.replace(new RegExp(`(^|[^0-9@])@${escapeRegExp(aliasNumber)}(?![0-9])`, 'g'), `$1@${number}`);
        }
    }
    return body;
}

/** Append the `@<number>` token for every jid whose token is missing from `text`
 *  (first-seen order, deduped, single-space separated). */
function injectMissingMentionTokens(text: string, mentionedJids: string[]): string {
    const body = text ?? '';
    const missing: string[] = [];
    const seen = new Set<string>();
    for (const jid of mentionedJids) {
        if (typeof jid !== 'string') continue;
        const number = bareNumber(jid);
        if (!number || seen.has(number)) continue;
        seen.add(number);
        if (!hasMentionToken(body, number)) missing.push(`@${number}`);
    }
    if (missing.length === 0) return body;
    const tokens = missing.join(' ');
    if (body.length === 0) return tokens;
    return /\s$/.test(body) ? `${body}${tokens}` : `${body} ${tokens}`;
}

function mintObjectId(): string {
    const seconds = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
    const random = Array.from(globalThis.crypto.getRandomValues(new Uint8Array(8)), (b) => b.toString(16).padStart(2, '0')).join('');
    return `${seconds}${random}`;
}

function byteLength(token: string): number {
    return new TextEncoder().encode(token).length;
}

/**
 * The wire messageSecret token of a message — the ONE rule. Both engines encode
 * it on send and the send-protocol store-check matches on it.
 *
 * The caller's `secret` when given, otherwise the message `_id`; only a token
 * that fits the marked layout (`callerTokenFits`, 1–28 bytes) is used:
 *   - caller `secret` > 28 bytes → the `_id` instead (the secret stays on the doc);
 *   - no usable `_id` (absent, empty, or > 28 bytes) → a freshly minted 24-hex
 *     ObjectId, never a token-less secret (the same constant for every such
 *     send, unmatchable).
 * Silent: `prepareSend` (the send path, once per send) logs the fallbacks.
 */
export function resolveWireSecret(message: { _id?: unknown; secret?: unknown }): string {
    const id = message?._id == null ? '' : String(message._id);
    const callerSecret = message?.secret == null || message.secret === '' ? undefined : String(message.secret);
    const preferred = callerSecret ?? id;
    if (callerTokenFits(preferred)) return preferred;
    if (callerSecret !== undefined && callerTokenFits(id)) return id;
    return mintObjectId();
}

/** Logs a `resolveWireSecret` fallback (the caller's secret too long, or a minted token). */
function warnWireSecretFallback(um: UserMessage, wireSecret: string): void {
    const secret = um.secret || undefined;
    if (secret !== undefined && wireSecret !== secret) {
        console.warn(`[prepareSend] caller secret is ${byteLength(secret)} bytes (> 28) — carrying the message id on the wire instead (the secret stays on the message doc)`);
    }
    if (wireSecret !== secret && wireSecret !== um._id) {
        console.warn(`[prepareSend] no usable message id (${byteLength(um._id ?? '')} bytes) — minted wire token ${wireSecret}`);
    }
}

export function prepareSend(um: UserMessage, opts: PrepareSendOptions = {}): PreparedSend {
    const chatId = um.contactList?.[0]?.id;
    if (!chatId) throw new EngineToolError('invalid_input', 'userMessage.contactList[0].id is required');

    const inline: string[] = [];
    const rewritten = (um.message ?? '').replace(INLINE_MENTION, (_match, _display: string, jid: string) => {
        const sent = opts.inlineJidOf?.[jid] ?? jid;
        inline.push(sent);
        return `@${bareNumber(sent)}`;
    });
    const mentions = [...new Set([...inline, ...(um.mentions ?? [])])];
    const body = rewriteAliasTokens(rewritten, mentions, opts.mentionAliases);

    const pollOptions = (um.poll?.options ?? [])
        .filter((o) => (o.text ?? '').trim() !== '')
        .sort((a, b) => a.order - b.order)
        .map(({ id, text }) => ({ id, text }));
    // A poll is sent only when `isPoll` is set (both engines' rule); then its options are required.
    if (um.isPoll && pollOptions.length === 0) throw new EngineToolError('invalid_input', 'userMessage.poll.options is required');
    const buttonReply = um.buttonReply ?? undefined;
    if (buttonReply) {
        // A selection with no menu is meaningless: never sent unquoted (both engines rely on this one check).
        if (!um.quotedMessageId) throw new EngineToolError('invalid_input', 'userMessage.quotedMessageId (the menu) is required with buttonReply');
        if ((um.message ?? '').trim() !== '' || um.asset?.url || um.isPoll) {
            throw new EngineToolError('invalid_input', 'userMessage.buttonReply is exclusive with message, asset and poll');
        }
    }
    const wireSecret = resolveWireSecret(um);
    warnWireSecretFallback(um, wireSecret);

    return {
        chatId,
        text: injectMissingMentionTokens(body, um.mentions ?? []),
        mentions,
        ...(um.isPoll ? { poll: { question: um.poll?.question ?? '', options: pollOptions, allowMultiple: um.poll?.allowMultiple === true } } : {}),
        ...(um.quotedMessageId ? { quotedKey: um.quotedMessageId } : {}),
        ...(buttonReply ? { buttonReply } : {}),
        ...(um.sendLinkPreview && um.linkPreview ? { linkPreview: um.linkPreview } : {}),
        ...(um.asset?.url ? { media: { url: um.asset.url, type: um.asset.type, name: um.asset.name } } : {}),
        wireSecret,
    };
}
